/**
 * BackgroundMusicTableV2 — BG Music list body, ported from the
 * prototype's `.screen-bgm` table (cms-redesign-mockup.html ~7226+,
 * styles 3203–3247).
 *
 * Columns:
 *   • Track    — bgm_title
 *   • Preview  — teal-soft pill play button + a decorative 12-bar
 *                waveform. Click the button to start audio playback
 *                of bgm_url (HTML <audio>); a single shared element
 *                is reused across rows so only one track plays at a
 *                time.
 *   • Duration — mm:ss from bgm_audio_length (seconds)
 *   • Actions  — hover-revealed Edit
 *
 * Data: reuses the v1 `fetchAllBGM` action — GET /background-musics
 * returns `{objects, count}` with `{bgm_id, bgm_title, bgm_url,
 * bgm_audio_length}` per row. No paginated mode; the list is small.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllBGM } from '../../../../actions/backgroundMusicActions';
import {
  LibTable,
  IconButton,
  cn,
} from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const PlayGlyph = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseGlyph = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" />
    <rect x="14" y="5" width="4" height="14" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '';
  const n = Math.max(0, Math.round(Number(seconds)));
  const min = Math.floor(n / 60);
  const sec = n % 60;
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
}

/* The 12-bar waveform from the prototype is purely decorative — heights
 * follow the same pseudo-random pattern (30/60/100/75/45/80/55/90/35/70/50/25).
 * `playing` lights the bars up to full opacity to signal active playback. */
const WAVEFORM_HEIGHTS = [30, 60, 100, 75, 45, 80, 55, 90, 35, 70, 50, 25];

function Waveform({ playing }) {
  return (
    <span
      aria-hidden
      className="inline-flex items-center align-middle"
      style={{ width: 70, height: 18, gap: 2, marginRight: 8 }}
    >
      {WAVEFORM_HEIGHTS.map((h, i) => (
        <span
          key={i}
          style={{
            flex: 1,
            minWidth: 2,
            maxWidth: 3,
            height: `${h}%`,
            background: '#75A293',
            opacity: playing ? 0.95 : 0.55,
            borderRadius: 1,
            transition: 'opacity 140ms ease',
          }}
        />
      ))}
    </span>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function BackgroundMusicTableV2() {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.background_musics.background_musics);

  // Single shared <audio> element so only one track plays at a time.
  // Pause + reset the previous one whenever a new row's play button
  // is pressed.
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    dispatch(fetchAllBGM());
  }, [dispatch]);

  useEffect(() => () => {
    // Tear down on unmount.
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  }, []);

  const togglePreview = (bgm) => {
    if (!bgm.bgm_url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', () => setPlayingId(null));
      audioRef.current.addEventListener('pause', () => {
        // Browser-initiated pause (e.g. tab background) — sync state.
        if (audioRef.current && audioRef.current.paused) setPlayingId(null);
      });
    }
    if (playingId === bgm.bgm_id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current.src = bgm.bgm_url;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {
      setPlayingId(null);
    });
    setPlayingId(bgm.bgm_id);
  };

  // Search lives in the global ⌘K palette; no in-page search input.
  const visible = Array.isArray(items) ? items : null;

  if (!Array.isArray(items)) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading background music…
      </div>
    );
  }

  return (
    <div>
      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No background music yet — add one to get started.
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* track */}
              <col />
              {/* preview */}
              <col style={{ width: '24%' }} />
              {/* duration */}
              <col style={{ width: '14%' }} />
              {/* actions */}
              <col style={{ width: '10%' }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Track</LibTable.Th>
                <LibTable.Th>Preview</LibTable.Th>
                <LibTable.NumTh>Duration</LibTable.NumTh>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {visible.map((bgm) => {
                const playing = playingId === bgm.bgm_id;
                return (
                  <LibTable.Row key={bgm.bgm_id}>
                    <LibTable.Td>
                      <Link
                        to={`/background-musics/edit/${bgm.bgm_id}`}
                        className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
                        style={{ fontSize: 13.5 }}
                        title={bgm.bgm_title || ''}
                      >
                        {bgm.bgm_title || 'Untitled'}
                      </Link>
                    </LibTable.Td>
                    <LibTable.Td>
                      <span className="inline-flex items-center align-middle">
                        <Waveform playing={playing} />
                        <button
                          type="button"
                          onClick={() => togglePreview(bgm)}
                          disabled={!bgm.bgm_url}
                          aria-label={playing ? 'Pause preview' : 'Play preview'}
                          className={cn(
                            'inline-grid place-items-center align-middle border cursor-pointer',
                            'disabled:opacity-40 disabled:cursor-not-allowed',
                          )}
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            background: playing ? '#75A293' : '#75A29318',
                            borderColor: 'transparent',
                            color: playing ? '#FFFFFF' : '#5C7E72',
                            transition: 'all 140ms ease',
                            transform: playing ? 'scale(1.06)' : 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (e.currentTarget.disabled) return;
                            e.currentTarget.style.background = '#75A293';
                            e.currentTarget.style.color = '#FFFFFF';
                            e.currentTarget.style.transform = 'scale(1.06)';
                          }}
                          onMouseLeave={(e) => {
                            if (e.currentTarget.disabled) return;
                            e.currentTarget.style.background = playing ? '#75A293' : '#75A29318';
                            e.currentTarget.style.color = playing ? '#FFFFFF' : '#5C7E72';
                            e.currentTarget.style.transform = playing ? 'scale(1.06)' : 'none';
                          }}
                        >
                          {playing ? PauseGlyph : PlayGlyph}
                        </button>
                      </span>
                    </LibTable.Td>
                    <LibTable.NumTd>
                      <span className="font-mono text-navy" style={{ fontSize: 13 }}>
                        {formatDuration(bgm.bgm_audio_length) || '—'}
                      </span>
                    </LibTable.NumTd>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                        <Link
                          to={`/background-musics/edit/${bgm.bgm_id}`}
                          aria-label={`Edit ${bgm.bgm_title}`}
                        >
                          <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                        </Link>
                      </div>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>
        </div>
      )}
    </div>
  );
}
